// ===========================================================================
// The account panel: sign in, create an account, or sign out.
//
// Signing in used to be reachable only through the Lumi Plus modal, so somebody
// who just wanted their own name back had to open a price list and read past
// subscriptions, cosmetics, and room upgrades to find the form. An account is
// optional on Lumi and has nothing to do with paying, so it gets its own door.
// The paid extras stay one click away for anyone who does want them. ASCII only.
// ===========================================================================

import { t } from '../i18n.js';
import { asModal } from './modal.js';
import { el, toast } from '../dom.js';
import { SERVER_HTTP } from '../config.js';
import { getKeepsake, forgetKeepsake } from '../keepsake.js';
import { renderAuth } from './auth.js';

/**
 * Open the account panel.
 * @param {import('../account.js').AccountClient} account
 * @param {{ onChange?: (account: object|null) => void, onPlus?: () => void, mode?: 'login'|'register' }} [opts]
 */
export function openAccount(account, opts = {}) {
  const onChange = opts.onChange || (() => {});
  const body = el('div', { class: 'billing-body' });

  const modal = el('div', { class: 'billing-modal account-modal' }, [
    el('div', { class: 'billing-head' }, [
      el('strong', { text: t('Your Lumi account') }),
      el('button', { class: 'btn btn-ghost btn-sm', text: t('Close'), onClick: () => dialog.close() }),
    ]),
    body,
    el('div', {
      class: 'billing-foot muted',
      text: t('An account is optional. Lumi works without one. Signing in only remembers your name, your Plus membership, and anything you have unlocked.'),
    }),
  ]);
  document.body.append(modal);
  // A dialog behaves like one: a backdrop that dims and blocks the page,
  // role and aria-modal, focus moved in and trapped, Escape to leave, and
  // focus put back where it came from. See ui/modal.js.
  const dialog = asModal(modal, { label: 'Your Lumi account' });

  // What Lumi remembers on THIS DEVICE, for everyone -- with or without an
  // account -- and the way out next to it. Said plainly: the whole point of a
  // keepsake is that it holds nothing a person would need to be told about,
  // and the honest way to prove that is to say exactly what it is.
  function keepsakeBlock() {
    const has = !!getKeepsake();
    const forgetBtn = el('button', {
      class: 'btn btn-ghost btn-sm',
      text: t('Forget me on this device'),
      onClick: async () => {
        forgetBtn.disabled = true;
        const confirmed = await forgetKeepsake({ base: SERVER_HTTP });
        // Storage is cleared either way; only the wording depends on whether
        // the server heard. An unheard key goes quiet and is swept in 90 days.
        toast(confirmed
          ? 'Forgotten. Lumi will not recognize this device again.'
          : 'Cleared on this device. Lumi will forget the rest within 90 days.');
        render();
      },
    });
    return el('div', { class: 'keepsake-block' }, [
      el('strong', { text: t('This device') }),
      el('p', {
        class: 'muted',
        text: t('Lumi remembers you on this device with a random key, so regulars and rooms can find you again. It holds no name and no personal details, and it is deleted after 90 days away.'),
      }),
      has
        ? el('div', { class: 'buy-row' }, [forgetBtn])
        : el('p', { class: 'muted', text: t('Nothing is remembered on this device yet.') }),
    ]);
  }

  function render() {
    body.replaceChildren();
    if (!account.loggedIn) {
      body.append(
        el('p', {
          class: 'muted',
          text: t('Signing in is instant. Creating an account is 18+ and includes a quick age check.'),
        }),
        // Defaults to the SIGN IN tab: someone opening this already has an
        // account far more often than not. The form's own tabs switch to
        // creating one.
        renderAuth(
          account,
          () => {
            onChange(account.account);
            render();
          },
          { mode: opts.mode === 'register' ? 'register' : 'login' },
        ),
      );
    } else {
      body.append(
        el('div', { class: 'billing-account' }, [
          el('span', { text: `Signed in as ${account.account.username}` }),
          account.isPlus ? el('span', { class: 'plus-badge', text: 'PLUS' }) : null,
        ].filter(Boolean)),
        el('p', {
          class: 'muted',
          text: account.isPlus
            ? 'You are a Plus member. Thank you for keeping Lumi independent and ad-free.'
            : 'You are on the free plan, which is all of Lumi. Plus is optional extras, never a gate.',
        }),
        el('div', { class: 'buy-row' }, [
          el('button', {
            class: 'btn btn-secondary btn-sm',
            text: account.isPlus ? 'Manage Plus and extras' : 'See Lumi Plus',
            onClick: () => {
              dialog.close();
              opts.onPlus?.();
            },
          }),
          el('button', {
            class: 'btn btn-ghost btn-sm',
            text: t('Sign out'),
            onClick: () => {
              account.logout();
              onChange(null);
              render();
            },
          }),
        ]),
      );
    }
    // Regulars are for everyone with an identity, account or keepsake, so the
    // door is here for both -- when the feature exists for this person at all.
    if (typeof opts.onRegulars === 'function') {
      body.append(
        el('div', { class: 'keepsake-block regulars-block' }, [
          el('strong', { text: t('Your regulars') }),
          el('p', { class: 'muted', text: t('People you have both waved at after a chat. You stay anonymous to each other.') }),
          el('div', { class: 'buy-row' }, [
            el('button', {
              class: 'btn btn-secondary btn-sm',
              text: opts.regularsCount ? `Regulars (${opts.regularsCount})` : 'Regulars',
              onClick: () => {
                dialog.close();
                opts.onRegulars();
              },
            }),
          ]),
        ]),
      );
    }
    // Below the account section for everyone: the device is remembered whether
    // or not a person ever signs in.
    body.append(keepsakeBlock());
  }

  render();
  return modal;
}

export default { openAccount };
